import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import RefreshIcon from '@mui/icons-material/CachedOutlined'
import GetAppIcon from '@mui/icons-material/GetApp'
import ListItemIcon from '@mui/material/ListItemIcon'
import Menu from '@mui/material/Menu'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import join from 'url-join'
import { Bar } from 'react-chartjs-2'
import { Chart as ChartJS, registerables } from 'chart.js'
import { getAppConfig } from 'app-config'
import { getAuthClient } from 'auth'
import { resilientFetch } from 'utils/resilientRequest'
import { useSelectedFleetIds } from 'features/settings'
import { DEFAULT_MONTH_SPAN } from '../config'
import { mapVehicleTypes } from '../effects'
import { actions as moduleActions } from '../module'
import VerticalAlignBottomTwoToneIcon from '@mui/icons-material/VerticalAlignBottomTwoTone'
import {
  ALL_MONTH_KEYS,
  BAR_GAP,
  BAR_WIDTH,
  DOWNLOAD_COLUMNS,
  FALLBACK_COLOUR,
  MIN_CHART_WIDTH,
  MONTHS,
  buildChartData,
  buildMonthRanges,
  chartOptions,
  checkboxSx,
  createInitialFilters,
  createPeriodLabel,
  deriveDefaultVehicleTypes,
  ensureVehicleTypeColours,
  extractAvailableDates,
  filterLabelSx,
  filterSelectSx,
  formatMonthParam,
  menuItemSx,
  menuListSx,
  menuPaperSx
} from './helpers'

ChartJS.register(...registerables)

dayjs.extend(utc)
dayjs.extend(timezone)

const VehicleEmissionsReport = () => {
  const selectedFleetIds = useSelectedFleetIds()
  const dispatch = useDispatch()

  const [filters, setFilters] = useState(() => {
    const initial = createInitialFilters()
    try {
      const params = new URLSearchParams(window.location.search)
      const range = params.get('range')
      if (range) {
        const parsed = dayjs(range, 'MMMM YYYY', true)
        if (parsed.isValid()) {
          const from = parsed.startOf('month')
          const to = parsed.endOf('month')
          initial.fromMonth = from.format('YYYY-MM-DD')
          initial.toMonth = to.format('YYYY-MM-DD')
          initial.periods = [
            {
              fromMonth: from.format('YYYY-MM-DD'),
              toMonth: to.format('YYYY-MM-DD'),
              label: createPeriodLabel(from, to)
            }
          ]
        }
      }
    } catch (err) {
      // ignore
    }
    return initial
  })
  const [downloading, setDownloading] = useState(false)
  const [localError, setLocalError] = useState('')

  const [selectedYears, setSelectedYears] = useState([])
  const [selectedMonths, setSelectedMonths] = useState([])
  const [selectedVehicleTypes, setSelectedVehicleTypes] = useState([])

  const [isDefaultView, setIsDefaultView] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      return !params.get('range')
    } catch {
      return true
    }
  })

  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null)
  const downloadMenuOpen = Boolean(downloadMenuAnchor)

  const handleDownloadMenuOpen = event => {
    setDownloadMenuAnchor(event.currentTarget)
  }

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null)
  }

  const {
    report: reportData = null,
    loading = false,
    error: apiError = null,
    infoMessage = '',
    lastUpdated: lastUpdatedAt = null
  } = useSelector(state => state.reportVehicleEmissions || {})

  const report = reportData

  const { availableYears, availableMonthsForSelection } = useMemo(() => {
    const backendYears = Array.isArray(report?.availableYears) ? report.availableYears : []
    const { years: dataYears, monthsByYear } = extractAvailableDates(report)
    const years = backendYears.length ? backendYears : dataYears

    if (!years.length) {
      return {
        availableYears: [],
        availableMonthsForSelection: ALL_MONTH_KEYS
      }
    }

    const monthSet = new Set()
    const yearsToCheck = selectedYears.length ? selectedYears : years
    yearsToCheck.forEach(y => {
      if (monthsByYear[y]) {
        monthsByYear[y].forEach(m => monthSet.add(m))
      }
    })
    const months = monthSet.size ? Array.from(monthSet).sort((a, b) => a - b) : ALL_MONTH_KEYS

    return { availableYears: years, availableMonthsForSelection: months }
  }, [report, selectedYears])

  const handleYearsChange = useCallback(event => {
    const { value } = event.target
    const next = (Array.isArray(value) ? value : [value])
      .map(Number)
      .filter(year => !Number.isNaN(year))

    setSelectedYears(next)
    setSelectedMonths([])
    setLocalError('')
  }, [])

  const handleMonthsChange = useCallback(event => {
    const { value } = event.target
    const next = (Array.isArray(value) ? value : [value])
      .map(Number)
      .filter(monthKey => !Number.isNaN(monthKey))

    setSelectedMonths(next)
  }, [])

  const handleApply = () => {
    if (!selectedYears.length && !selectedVehicleTypes.length) {
      setLocalError('Select at least one year or vehicle type')
      return
    }

    setIsDefaultView(false)
    setLocalError('')

    const monthsToUse = selectedMonths.length > 0 ? selectedMonths : ALL_MONTH_KEYS
    const ranges = buildMonthRanges(selectedYears, monthsToUse)

    const periods = ranges.map(range => {
      const from = range.from.startOf('month')
      const to = range.to.endOf('month')
      return {
        fromMonth: from.format('YYYY-MM-DD'),
        toMonth: to.format('YYYY-MM-DD'),
        label: createPeriodLabel(from, to)
      }
    })

    const newFilters = {
      ...filters,
      periods,
      vehicleTypes: selectedVehicleTypes || []
    }

    setFilters(newFilters)

    const payload = {
      fleetIds: selectedFleetIds,
      periods: periods.map(period => ({
        fromMonth: dayjs(period.fromMonth).startOf('month')
          .format('YYYY-MM-DD'),
        toMonth: dayjs(period.toMonth).endOf('month')
          .format('YYYY-MM-DD')
      })),
      vehicleTypes: newFilters.vehicleTypes
    }

    dispatch(moduleActions.fetchReport(payload))
  }

  const createFetchPayload = useCallback(() => {
    if (!selectedFleetIds.length) {
      return null
    }

    if (Array.isArray(filters.periods) && filters.periods.length) {
      return {
        fleetIds: selectedFleetIds,
        periods: filters.periods.map(period => ({
          fromMonth: dayjs(period.fromMonth).startOf('month')
            .format('YYYY-MM-DD'),
          toMonth: dayjs(period.toMonth).endOf('month')
            .format('YYYY-MM-DD')
        })),
        vehicleTypes: filters.vehicleTypes
      }
    }

    const toMonthDate = filters.toMonth
      ? dayjs(filters.toMonth).endOf('month')
      : dayjs().endOf('month')
    const fromMonthDate = filters.fromMonth
      ? dayjs(filters.fromMonth).startOf('month')
      : toMonthDate.clone().subtract(DEFAULT_MONTH_SPAN - 1, 'month')
        .startOf('month')

    return {
      fleetIds: selectedFleetIds,
      fromMonth: fromMonthDate.format('YYYY-MM-DD'),
      toMonth: toMonthDate.format('YYYY-MM-DD'),
      vehicleTypes: filters.vehicleTypes
    }
  }, [
    filters.fromMonth,
    filters.periods,
    filters.toMonth,
    filters.vehicleTypes,
    selectedFleetIds
  ])

  useEffect(() => {
    const payload = createFetchPayload()
    if (!payload) {
      return
    }
    dispatch(moduleActions.fetchReport(payload))
  }, [createFetchPayload, dispatch])

  const handleRefresh = useCallback(() => {
    const payload = createFetchPayload()
    if (!payload) {
      return
    }
    dispatch(moduleActions.fetchReport(payload))
  }, [createFetchPayload, dispatch])

  const handleDownload = useCallback(async (downloadAll = false) => {
    handleDownloadMenuClose()

    if (!selectedFleetIds.length || downloading) {
      if (!selectedFleetIds.length) {
        setLocalError('Please select at least one fleet before downloading.')
      }
      return
    }

    try {
      setDownloading(true)
      setLocalError('')

      const config = getAppConfig()
      const authClient = getAuthClient()
      const token = await authClient.getTokenSilently()

      const params = new URLSearchParams()
      params.append('fleetId', selectedFleetIds.join(','))

      // Only apply period/date filters if not downloading all
      if (!downloadAll) {
        if (filters.periods?.length) {
          filters.periods.forEach((period, idx) => {
            params.append(
              `periods[${idx}].fromMonth`,
              dayjs(period.fromMonth).startOf('month')
                .format('YYYY-MM-DD')
            )
            params.append(
              `periods[${idx}].toMonth`,
              dayjs(period.toMonth).endOf('month')
                .format('YYYY-MM-DD')
            )
          })
        } else {
          const fromParam = formatMonthParam(filters.fromMonth)
          if (fromParam) {
            params.append('fromMonth', fromParam)
          }

          const toParam = formatMonthParam(filters.toMonth, true)
          if (toParam) {
            params.append('toMonth', toParam)
          }
        }

        filters.vehicleTypes.forEach(type => params.append('vehicleTypes', type))
      }

      DOWNLOAD_COLUMNS.forEach(column => params.append('columns', column))

      const requestUrl = join(config.app.api, '/download/vehicleemissions')
      const response = await resilientFetch(`${requestUrl}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`)
      }

      const blob = await response.blob()
      const fileName = `vehicle-emissions-${downloadAll ? 'all-' : ''}${dayjs().format('YYYY-MM-DD-HHmm')}.xlsx`
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(objectUrl)
    } catch (error) {
      setLocalError(error?.message || 'Unable to download emissions report.')
    } finally {
      setDownloading(false)
    }
  }, [
    filters.fromMonth,
    filters.toMonth,
    filters.periods,
    filters.vehicleTypes,
    selectedFleetIds,
    downloading
  ])

  const allSeenTypesRef = useRef(new Map())

  const availableTypes = useMemo(() => {
    const incoming = report?.vehicleTypes?.length
      ? mapVehicleTypes(report.vehicleTypes)
      : deriveDefaultVehicleTypes()

    incoming.forEach(t => {
      if (!allSeenTypesRef.current.has(t.key)) {
        allSeenTypesRef.current.set(t.key, t)
      }
    })

    return ensureVehicleTypeColours(Array.from(allSeenTypesRef.current.values()))
  }, [report])

  const activeVehicleTypes = useMemo(() => {
    if (!availableTypes?.length) {
      return []
    }
    return filters.vehicleTypes.length
      ? ensureVehicleTypeColours(
        availableTypes.filter(type => filters.vehicleTypes.includes(type.key))
      )
      : availableTypes
  }, [availableTypes, filters.vehicleTypes])

  const chartData = useMemo(
    () => buildChartData(report, activeVehicleTypes, { autoWindow: isDefaultView }),
    [activeVehicleTypes, report, isDefaultView]
  )

  const chartCanvasMinWidth = useMemo(() => {
    const barCount = chartData?.labels?.length || 0
    return Math.max(barCount * (BAR_WIDTH + BAR_GAP), MIN_CHART_WIDTH)
  }, [chartData])

  const displayedError = localError || apiError
  const lastUpdatedLabel = lastUpdatedAt
    ? dayjs(lastUpdatedAt).format('DD MMM YYYY, HH:mm')
    : null

  const handleVehicleTypeChange = event => {
    const value = event.target.value || []
    const next = Array.isArray(value) ? value : [value]
    setSelectedVehicleTypes(next)
  }


  const handleResetFilters = () => {
    allSeenTypesRef.current = new Map()
    setIsDefaultView(true)
    setFilters(createInitialFilters())
    setSelectedYears([])
    setSelectedMonths([])
    setSelectedVehicleTypes([])
    setLocalError('')
  }

  return (
    <Stack spacing={3} sx={{ padding: 3 }}>
      <Card
        sx={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
          border: 'none'
        }}>
        <CardContent sx={{ py: 2, px: 0 }}>
          <Grid container alignItems='center'>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <Typography sx={filterLabelSx}>
                  Year (of emission record)
                </Typography>
                <Select
                  multiple
                  displayEmpty
                  value={selectedYears}
                  onChange={handleYearsChange}
                  sx={filterSelectSx}
                  renderValue={selected => {
                    if (selected.length === 0) {
                      return 'Choose Year'
                    }
                    return selected.join(', ')
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        marginTop: 1,
                        borderRadius: '8px'
                      }
                    }
                  }}>
                  {availableYears.map(year => (
                    <MenuItem
                      key={year}
                      value={year}
                      sx={{
                        py: 1.3,
                        my: 0.5
                      }}>
                      <Checkbox
                        checked={selectedYears.includes(year)}
                        sx={{
                          p: 0,
                          mr: 2,
                          color: '#C5CBD3',
                          '&.Mui-checked': {
                            color: '#8CAEB8'
                          }
                        }}/>

                      <ListItemText
                        primary={year}
                        primaryTypographyProps={{
                          fontSize: '15px',
                          fontWeight: 500
                        }}/>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={0.2} />
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <Typography sx={filterLabelSx}>
                  Fuel Date (of emission record)
                </Typography>
                <Select
                  multiple
                  displayEmpty
                  value={selectedMonths}
                  onChange={handleMonthsChange}
                  sx={filterSelectSx}
                  renderValue={selected => {
                    if (!selected.length) {
                      return 'All months'
                    }
                    return selected.map(monthKey => MONTHS[monthKey - 1].label).join(', ')
                  }}
                  MenuProps={{
                    PaperProps: { sx: menuPaperSx },
                    MenuListProps: { sx: menuListSx }
                  }}>
                  {MONTHS.filter(month => availableMonthsForSelection.includes(month.key)).map(month => (
                    <MenuItem key={month.key} value={month.key} sx={menuItemSx}>
                      <Checkbox
                        checked={selectedMonths.includes(month.key)}
                        sx={checkboxSx}/>
                      <ListItemText
                        primary={month.label}
                        primaryTypographyProps={{
                          fontSize: '15px',
                          fontWeight: 500
                        }}/>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={0.2} />
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <Typography sx={filterLabelSx}>Vehicle type</Typography>
                <Select
                  multiple
                  displayEmpty
                  value={selectedVehicleTypes}
                  onChange={handleVehicleTypeChange}
                  sx={filterSelectSx}
                  renderValue={selected =>
                    selected.length
                      ? selected
                        .map(k => {
                          const found = availableTypes.find(t => t.key === k)
                          return found ? found.label : k
                        })
                        .join(', ')
                      : 'Choose vehicle types'
                  }
                  MenuProps={{
                    PaperProps: { sx: menuPaperSx },
                    MenuListProps: { sx: menuListSx }
                  }}>
                  {availableTypes.map(type => (
                    <MenuItem key={type.key} value={type.key} sx={menuItemSx}>
                      <Checkbox
                        checked={selectedVehicleTypes.includes(type.key)}
                        sx={checkboxSx}/>
                      <ListItemText
                        primary={type.label}
                        primaryTypographyProps={{
                          fontSize: '15px',
                          fontWeight: 500
                        }}/>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md />
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                color='primary'
                variant='contained'
                onClick={handleApply}>
                Apply
              </Button>
            </Grid>
            <Grid item xs={12} md={0.2} />

            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                color='primary'
                variant='contained'
                onClick={handleResetFilters}>
                Reset Filters
              </Button>
            </Grid>
          </Grid>

        </CardContent>
      </Card>

      {displayedError && <Alert severity='error'>{displayedError}</Alert>}
      {!displayedError && infoMessage && (
        <Alert severity='info'>{infoMessage}</Alert>
      )}

      <Card sx={{ p: 2 }}>
        <CardHeader
          sx={{ p: 1 }}
          title={
            <Typography variant='h4' fontWeight={600}>
              CO2 Emissions (tons/month)
            </Typography>
          }
          action={
            <Stack direction='row' spacing={1} alignItems='center'>
              <Tooltip title='Download'>
                <IconButton size='small' onClick={handleDownloadMenuOpen}>
                  <VerticalAlignBottomTwoToneIcon sx={{ color: 'black' }} />
                </IconButton>
              </Tooltip>
              <Menu
                id='download-menu'
                anchorEl={downloadMenuAnchor}
                open={downloadMenuOpen}
                onClose={handleDownloadMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                <MenuItem onClick={() => handleDownload(true)}>
                  <ListItemIcon>
                    <GetAppIcon sx={{ color: 'black' }} />
                  </ListItemIcon>
                  <ListItemText>
                    <Typography variant='bodyLarge'>
                      Download All
                    </Typography>
                  </ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleDownload(false)}>
                  <ListItemIcon>
                    <GetAppIcon sx={{ color: 'black' }} />
                  </ListItemIcon>
                  <ListItemText>
                    <Typography variant='bodyLarge'>
                      Download Filtered
                    </Typography>
                  </ListItemText>
                </MenuItem>
              </Menu>
              <Tooltip title='Refresh'>
                <span>
                  <IconButton onClick={handleRefresh} disabled={loading}>
                    {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          }/>
        <Box
          sx={{
            height: '1px',
            width: '100%',
            backgroundColor: '#E0E3E8',
            mt: 1,
            mb: 2
          }}/>

        {lastUpdatedLabel && (
          <Typography
            variant='body2'
            sx={{
              display: 'block',
              mt: 1,
              mb: 2,
              color: '#000000',
              px: 1
            }}>
            Last updated {lastUpdatedLabel}
          </Typography>
        )}

        <CardContent>
          <Box sx={{ minHeight: 360, position: 'relative' }}>
            {loading && (
              <Stack
                alignItems='center'
                justifyContent='center'
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1
                }}>
                <CircularProgress />
              </Stack>
            )}

            {!loading && chartData && (
              <>
                <Box sx={{ overflowX: 'auto' }}>
                  <Box
                    sx={{
                      width: `${chartCanvasMinWidth}px`,
                      minWidth: `${chartCanvasMinWidth}px`,
                      height: 361
                    }}>
                    <Bar data={chartData} options={chartOptions} />
                  </Box>
                </Box>

                {activeVehicleTypes.length ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 8 }}>
                    {activeVehicleTypes.map(type => (
                      <Stack
                        key={type.key}
                        direction='row'
                        spacing={2}
                        alignItems='center'>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: 6,
                            bgcolor: type.color || FALLBACK_COLOUR,
                            border: '1px solid rgba(58, 29, 29, 0.12)'
                          }}/>
                        <Typography variant='body2'>{type.label}</Typography>
                      </Stack>
                    ))}
                  </Box>
                ) : null}
              </>
            )}

            {!loading && !chartData && (
              <Stack
                alignItems='center'
                justifyContent='center'
                sx={{ height: 240 }}>
                <Typography>
                  No emissions data available for the selected filters.
                </Typography>
              </Stack>
            )}
          </Box>
        </CardContent>
      </Card>
    </Stack>
  )
}

export default VehicleEmissionsReport
